import React, { useState, useEffect } from 'react';
import Sidebar from '../../../components/shared/Sidebar';
import PageLoading from '../../../components/shared/PageLoading';
import Button from '../../../components/shared/Button';
import StatusBadge from '../../../components/shared/StatusBadge';
import { getCSRFToken } from '../../../utils/api';

/**
 * USOKIE Platform IP Objects Registry
 * Design: Black (#000), Bone (#fffbf7), Sage (#a1c6a2)
 */
const IpObjectList = () => {
  const [ipObjects, setIpObjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [categories, setCategories] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    fetchIpObjects();
  }, [searchQuery, statusFilter, categoryFilter, currentPage]);

  const fetchCategories = async () => {
    try {
      const response = await fetch('/api/v1/categories');
      const data = await response.json();
      setCategories(data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchIpObjects = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: currentPage,
        per_page: 25,
        ...(searchQuery && { q: searchQuery }),
        ...(statusFilter && { status: statusFilter }),
        ...(categoryFilter && { category_id: categoryFilter })
      });

      const response = await fetch(`/api/v1/ip_objects?${params}`);
      const data = await response.json();

      setIpObjects(data.ip_objects || []);
      setTotalPages(data.meta?.total_pages || 1);
      setTotalCount(data.meta?.total_count || 0);
    } catch (error) {
      console.error('Error fetching IP objects:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };

  const handleStatusFilter = (e) => {
    setStatusFilter(e.target.value);
    setCurrentPage(1);
  };

  const handleCategoryFilter = (e) => {
    setCategoryFilter(e.target.value);
    setCurrentPage(1);
  };

  const handleDelete = async (id) => {
    if (!confirm('Are you sure you want to delete this IP object?')) return;

    try {
      const response = await fetch(`/api/v1/ip_objects/${id}`, {
        method: 'DELETE',
        headers: {
          'X-CSRF-Token': getCSRFToken()
        }
      });

      if (response.ok) {
        fetchIpObjects();
      } else {
        alert('Error deleting IP object');
      }
    } catch (error) {
      console.error('Error deleting IP object:', error);
      alert('Error deleting IP object');
    }
  };

  if (loading && ipObjects.length === 0) {
    return <PageLoading message="Loading Registry" />;
  }

  return (
    <div className="flex h-screen bg-black">
      <Sidebar />

      <main className="flex-1 overflow-auto">
        <div className="max-w-7xl mx-auto p-8">
          {/* Document Header */}
          <div className="mb-8 pb-6 border-b border-[rgba(255,251,247,0.2)]">
            <div className="flex items-end justify-between">
              <div>
                <span
                  className="text-[10px] text-[rgba(255,251,247,0.4)] tracking-[0.2em] uppercase block mb-2"
                  style={{ fontFamily: "'GT Pressura Mono', monospace" }}
                >
                  REGISTRY IP.LIST
                </span>
                <h1 className="text-2xl font-bold text-[#fffbf7] tracking-[0.08em] uppercase">
                  IP Objects Registry
                </h1>
              </div>
              <Button href="/ip_objects/new">
                + New IP Object
              </Button>
            </div>
          </div>

          {/* Filters */}
          <div className="border border-[rgba(255,251,247,0.1)] bg-[#0a0a0a] p-6 mb-6">
            <div className="flex items-center gap-2 mb-4">
              <span
                className="text-xs text-[#a1c6a2] tracking-wider"
                style={{ fontFamily: "'GT Pressura Mono', monospace" }}
              >
                FILTERS
              </span>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <input
                type="text"
                placeholder="Search by name..."
                value={searchQuery}
                onChange={handleSearch}
                className="w-full px-4 py-3 border border-[rgba(255,251,247,0.2)] bg-black text-[#fffbf7] text-sm focus:outline-none focus:border-[#a1c6a2] transition-colors placeholder:text-[rgba(255,251,247,0.3)]"
              />
              <select
                value={statusFilter}
                onChange={handleStatusFilter}
                className="w-full px-4 py-3 border border-[rgba(255,251,247,0.2)] bg-black text-[#fffbf7] text-sm focus:outline-none focus:border-[#a1c6a2] transition-colors"
              >
                <option value="">All Statuses</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="pending">Pending</option>
              </select>
              <select
                value={categoryFilter}
                onChange={handleCategoryFilter}
                className="w-full px-4 py-3 border border-[rgba(255,251,247,0.2)] bg-black text-[#fffbf7] text-sm focus:outline-none focus:border-[#a1c6a2] transition-colors"
              >
                <option value="">All Categories</option>
                {categories.map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Results Info */}
          <div className="mb-6">
            <span
              className="text-[10px] text-[rgba(255,251,247,0.4)] tracking-[0.15em]"
              style={{ fontFamily: "'GT Pressura Mono', monospace" }}
            >
              {totalCount} {totalCount === 1 ? 'RESULT' : 'RESULTS'} FOUND
            </span>
          </div>

          {/* Loading Overlay */}
          {loading && (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border border-[rgba(255,251,247,0.2)] border-t-[#a1c6a2] animate-spin" />
            </div>
          )}

          {/* Grid */}
          {!loading && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {ipObjects.length === 0 ? (
                <div className="col-span-full border border-[rgba(255,251,247,0.1)] bg-[#0a0a0a] p-12 text-center">
                  <p className="text-sm text-[rgba(255,251,247,0.5)]">
                    No IP objects found. Create your first one!
                  </p>
                </div>
              ) : (
                ipObjects.map(ipObject => (
                  <div
                    key={ipObject.id}
                    className="border border-[rgba(255,251,247,0.1)] bg-[#0a0a0a] hover:border-[rgba(255,251,247,0.2)] transition-colors"
                  >
                    {/* Card Header */}
                    <div className="border-b border-[rgba(255,251,247,0.1)] px-6 py-4 bg-black">
                      <div className="flex items-center justify-between">
                        <a
                          href={`/ip_objects/${ipObject.id}`}
                          className="text-sm font-semibold text-[#fffbf7] hover:text-[#a1c6a2] transition-colors tracking-wide"
                        >
                          {ipObject.name}
                        </a>
                        <StatusBadge status={ipObject.status} />
                      </div>
                    </div>

                    {/* Card Body */}
                    <div className="p-6">
                      <p className="text-sm text-[rgba(255,251,247,0.6)] mb-4 line-clamp-2">
                        {ipObject.description || 'No description'}
                      </p>
                      <div className="flex justify-between text-sm">
                        <span
                          className="text-[10px] text-[rgba(255,251,247,0.4)] tracking-wider uppercase"
                          style={{ fontFamily: "'GT Pressura Mono', monospace" }}
                        >
                          Category
                        </span>
                        <span className="text-[rgba(255,251,247,0.6)]">
                          {ipObject.category?.name || '-'}
                        </span>
                      </div>
                    </div>

                    {/* Card Actions */}
                    <div className="flex gap-2 px-6 py-4 border-t border-[rgba(255,251,247,0.1)] bg-black">
                      <Button href={`/ip_objects/${ipObject.id}`} variant="secondary" size="sm">
                        View
                      </Button>
                      <Button href={`/ip_objects/${ipObject.id}/edit`} variant="secondary" size="sm">
                        Edit
                      </Button>
                      <Button variant="danger" size="sm" onClick={() => handleDelete(ipObject.id)}>
                        Delete
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-6 mt-8 pt-6 border-t border-[rgba(255,251,247,0.1)]">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                Previous
              </Button>
              <span
                className="text-[10px] text-[rgba(255,251,247,0.5)] tracking-wider"
                style={{ fontFamily: "'GT Pressura Mono', monospace" }}
              >
                Page {currentPage} of {totalPages}
              </span>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
              >
                Next
              </Button>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default IpObjectList;
